import {getStartObject} from '../config/startObject';
import getRegionNameFromCountryCode from '../utils/CountryCodes/getRegionNameFromCountryCode';
import Track from '../utils/tracking';
import {getStores} from '../actions/APIActions/CheckoutAPIActions';
import {get, post} from './ajax';
import geocode from './geocode';

function getPudoStatusForCountry(country) {
  return get(`/checkout-api/v2/click-and-collect/availability?country=${country}`)
    .then(result => result.value === 'AVAILABLE')
    .catch(() => false);
}

const geocodeLatlng = latlng => geocode({location: latlng});

/* eslint-disable camelcase */// -- Google Maps API identifiers, so casing is unavoidable.
class PudoApi {
  static async getBoundingRegion(countryCode) {
    const searchBounds = getRegionNameFromCountryCode(countryCode.toUpperCase());
    const results = await geocode({address: searchBounds, region: searchBounds});
    
    try {
      return results.find(result => result.types.includes('country'))
        .geometry.viewport;
    } catch (err) {
      Track.error('Google Maps API geocode service error - failed to set bounding region');
    }
  }
  
  static async getAddress(latlng) {
    const results = await geocodeLatlng(latlng);
    
    const addressComponents = results.flatMap(result => result.address_components);
    
    const getField = (fieldType, excluding) => addressComponents.find((component) => {
      const {types} = component;
      return types.includes(fieldType) && !types.includes(excluding);
    })?.short_name;
    
    const addressData = {
      latlng,
      address: results[0].formatted_address,
      postcode: getField('postal_code', 'postal_code_prefix'),
      country: getField('country'),
      locality: getField('postal_town') || getField('locality') || getField('administrative_area_level_2'),
    };
    
    if (!addressData.postcode) {
      addressData.postcode = getField('postal_code_prefix');
    }
    
    return addressData;
  }
  
  static getAllPudoSavedAddresses() {
    return get('/checkout-api/v2/addresses/click-and-collect');
  }
  
  static getCurrentLocation() {
    return new Promise((resolve, reject) => {
      if ('geolocation' in navigator) {
        const options = {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        };
        
        navigator.geolocation.getCurrentPosition((position) => {
          const currentLocation = {};
          currentLocation.latlng = {lat: position.coords.latitude, lng: position.coords.longitude};
          
          this.getAddress(currentLocation.latlng).then((address) => {
            currentLocation.address = address.address;
            currentLocation.postcode = address.postcode;
            currentLocation.country = address.country;
            currentLocation.locality = address.locality;
            resolve(currentLocation);
          }).catch(reject);
        }, reject, options);
      } else {
        reject(false);
      }
    });
  }
  
  static getCollectionPoints(country, postcode = '', locality, latlng) {
    const clickAndCollectDeliveryLocation = {
      country,
      postcode,
      locality,
      latitude: latlng.lat,
      longitude: latlng.lng,
    };
    return post('/checkout-api/v2/click-and-collect/locations', clickAndCollectDeliveryLocation);
  }
  
  static getStores(country, postcode = '', locality, latlng, dispatch) {
    const clickAndCollectDeliveryLocation = {
      country,
      postcode,
      locality,
      latitude: latlng.lat,
      longitude: latlng.lng,
    };
    return dispatch(getStores(clickAndCollectDeliveryLocation)).then((storeResponse) => {
      if (storeResponse?.status === 200 && storeResponse?.msg?.length > 0) {
        return storeResponse.msg;
      }
      
      return new Error('Error to find stores');
    });
  }
  
  static async getPudoStatus(country) {
    const {settings} = getStartObject();
    
    if (settings?.click_and_collect_enabled) {
      return getPudoStatusForCountry(country);
    }
    
    return false;
  }
  
  static saveNewCollectionPoint(data) {
    return post('/checkout-api/v2/addresses/click-and-collect', data);
  }
}

export default PudoApi;
